import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { toast } from '../../toasts';
import axiosInstance from '../../RestClient';
import contractTemplateService from '../../services/contractTemplate.service';
import ConfigService from '../../services/settings.service';
import { getError } from '../../utils';
import PropTypes from 'prop-types';
import SpinnerOverlay from '../SpinnerOverlay/SpinnerOverlay';
import { Button, FormField, Select, controlClass } from '../EntityList';
import { isEsignatureEnabled } from '../../utils/featureFlags';
import { useLabels } from '../../hooks/useLabels';
import SendForSignatureModal from '../SendForSignatureModal/SendForSignatureModal';

const normalizePhone = (raw) => raw?.replace(/\D/g, '') || '';
const isValidPhone = (raw) => {
	const digits = normalizePhone(raw);
	return digits.length === 11 && digits.startsWith('598');
};

const CATEGORY_FOR_TYPE = {
	contract: 'alquiler',
	assetSale: 'venta',
	asset: 'activo',
};

// GenerateContractModal's entityType convention ('asset') differs from
// SingleObjectDocumentManager's objectType convention ('escritura') used
// everywhere else in the app — translate before opening the signing wizard.
const ENTITY_TYPE_TO_OBJECT_TYPE = {
	contract: 'contract',
	assetSale: 'assetSale',
	asset: 'escritura',
};

export default function GenerateContractModal({ show, onHide, entityType, entityId }) {
	const { t } = useLabels();
	const [templates, setTemplates] = useState([]);
	const [selectedTemplateId, setSelectedTemplateId] = useState('');
	const [format, setFormat] = useState('docx');
	const [loading, setLoading] = useState(false);
	const [generating, setGenerating] = useState(false);
	const [sharing, setSharing] = useState(false);
	const [phoneInput, setPhoneInput] = useState('');
	const [phoneLoading, setPhoneLoading] = useState(false);
	const [companyName, setCompanyName] = useState('');
	const [generatedDocument, setGeneratedDocument] = useState(null);
	const [showSignModal, setShowSignModal] = useState(false);

	useEffect(() => {
		if (!show) return;
		setPhoneInput('');
		setGeneratedDocument(null);
		const fetchData = async () => {
			setLoading(true);
			setPhoneLoading(true);
			try {
				const category = CATEGORY_FOR_TYPE[entityType];
				const [templatesRes, phoneRes, settingsRes] = await Promise.allSettled([
					contractTemplateService.list(category ? { category } : {}),
					contractTemplateService.getEntityPhone({ entityType, entityId }),
					ConfigService.list(),
				]);
				if (templatesRes.status === 'fulfilled') {
					setTemplates(templatesRes.value.data);
					if (templatesRes.value.data.length > 0) setSelectedTemplateId(templatesRes.value.data[0]._id);
				} else {
					toast.error(getError(templatesRes.reason));
				}
				if (phoneRes.status === 'fulfilled' && phoneRes.value.data.clientPhone) {
					setPhoneInput(phoneRes.value.data.clientPhone);
				}
				if (settingsRes.status === 'fulfilled') {
					setCompanyName(settingsRes.value.data?.companyName || '');
				}
			} finally {
				setLoading(false);
				setPhoneLoading(false);
			}
		};
		fetchData();
	}, [show, entityType, entityId]);

	const handleGenerate = async () => {
		if (!selectedTemplateId) {
			toast.error('Seleccione una plantilla');
			return;
		}
		try {
			setGenerating(true);
			const response = await axiosInstance.post(
				`/contractTemplates/generate/${selectedTemplateId}`,
				{ entityType, entityId, format },
				{ responseType: 'blob' }
			);
			const mimeType = format === 'pdf'
				? 'application/pdf'
				: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
			const fallbackName = format === 'pdf' ? 'contrato.pdf' : 'contrato.docx';
			const blob = new Blob([response.data], { type: mimeType });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			const filename = response.headers['content-disposition']
				?.split('filename=')[1]
				?.replace(/"/g, '') || fallbackName;
			link.download = filename;
			link.click();
			URL.revokeObjectURL(url);
			toast.success('Documento generado');
			// X-Document-Id is only set on the PDF branch (routes/contractTemplateRoutes.js)
			// — lets us chain straight into "Enviar a firmar" without a re-fetch.
			const documentId = response.headers['x-document-id'];
			if (format === 'pdf' && documentId && isEsignatureEnabled()) {
				setGeneratedDocument({ _id: documentId, name: filename });
			} else {
				onHide();
			}
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setGenerating(false);
		}
	};

	const handleSendWhatsApp = async () => {
		if (!selectedTemplateId) {
			toast.error('Seleccione una plantilla');
			return;
		}
		if (!isValidPhone(phoneInput)) {
			toast.error('Ingrese un número de WhatsApp válido con código de país (ej: 59891234567)');
			return;
		}
		try {
			setSharing(true);
			const res = await contractTemplateService.createShareLink({
				templateId: selectedTemplateId,
				entityType,
				entityId,
				format,
			});
			const { url } = res.data;
			const digits = normalizePhone(phoneInput);
			const greeting = companyName ? `${companyName} - Hola` : 'Hola';
			const text = encodeURIComponent(`${greeting}, te comparto el contrato para descargar: ${url}`);
			window.open(`https://wa.me/${digits}?text=${text}`, '_blank', 'noopener,noreferrer');
			toast.success('Enlace generado');
			onHide();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setSharing(false);
		}
	};

	return (
		<>
			<Modal show={show} onHide={onHide} centered>
				<Modal.Header className='tw:border-b tw:border-border tw:bg-surface-2 tw:px-5 tw:py-4' closeButton={!(loading || generating || sharing)}>
					<Modal.Title className='tw:text-[15px] tw:font-semibold tw:text-ink-0'>Generar Documento</Modal.Title>
				</Modal.Header>
				<Modal.Body className='tw:relative tw:bg-surface tw:px-5 tw:py-5'>
					{(loading || generating || sharing) && <SpinnerOverlay />}
					{generatedDocument ? (
						<p className='tw:m-0 tw:text-[13px] tw:text-ink-1'>{t('esignature.modal.generatedPrompt')}</p>
					) : templates.length === 0 ? (
						<p className='tw:m-0 tw:text-[13px] tw:text-ink-2'>No hay plantillas disponibles para este tipo de registro.</p>
					) : (
						<div className='tw:flex tw:flex-col tw:gap-4'>
							<FormField id='generate-template' label='Seleccionar plantilla' required>
								<Select
									id='generate-template'
									options={templates.map((template) => ({ value: template._id, label: template.name }))}
									value={selectedTemplateId}
									onChange={(e) => setSelectedTemplateId(e.target.value)}
									disabled={loading || generating || sharing}
								/>
							</FormField>
							<FormField id='generate-format' label='Formato' required>
								<Select
									id='generate-format'
									options={[
										{ value: 'docx', label: 'Word (.docx)' },
										{ value: 'pdf', label: 'PDF' },
									]}
									value={format}
									onChange={(e) => setFormat(e.target.value)}
									disabled={loading || generating || sharing}
								/>
							</FormField>
							<FormField id='generate-phone' label='Número de WhatsApp'>
								<input
									id='generate-phone'
									type='tel'
									placeholder='59891234567'
									value={phoneInput}
									onChange={(e) => setPhoneInput(e.target.value)}
									disabled={phoneLoading || generating || sharing}
									aria-invalid={!!phoneInput && !isValidPhone(phoneInput)}
									className={`${controlClass} tw:w-full ${phoneInput && !isValidPhone(phoneInput) ? 'tw:border-danger' : ''}`}
								/>
								<p className='tw:mt-1.5 tw:mb-0 tw:text-[12px] tw:text-ink-2'>
								Incluir código de país. Ej: 59891234567 (Uruguay: 598 + 8 dígitos)
								</p>
								{phoneInput && !isValidPhone(phoneInput) && (
									<p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>Número inválido. Ej: 59891234567</p>
								)}
							</FormField>
						</div>
					)}
				</Modal.Body>
				<Modal.Footer className='tw:flex tw:flex-wrap tw:gap-2 tw:border-t tw:border-border tw:bg-surface-2 tw:px-5 tw:py-3.5'>
					{generatedDocument ? (
						<>
							<Button onClick={onHide}>
								{t('action.cancel')}
							</Button>
							<Button variant='primary' onClick={() => setShowSignModal(true)}>
								{t('esignature.send')}
							</Button>
						</>
					) : (
						<>
							<Button onClick={onHide} disabled={generating || sharing}>
								Cancelar
							</Button>
							<Button
								onClick={handleSendWhatsApp}
								disabled={sharing || generating || loading || phoneLoading || !selectedTemplateId}
							>
								Enviar por WhatsApp
							</Button>
							<Button
								variant='primary'
								onClick={handleGenerate}
								disabled={generating || sharing || loading || !selectedTemplateId}
							>
								Generar Documento
							</Button>
						</>
					)}
				</Modal.Footer>
			</Modal>
			{isEsignatureEnabled() && generatedDocument && (
				<SendForSignatureModal
					show={showSignModal}
					onHide={() => setShowSignModal(false)}
					document={generatedDocument}
					objectType={ENTITY_TYPE_TO_OBJECT_TYPE[entityType]}
					objectId={entityId}
					onSent={() => {
						setShowSignModal(false);
						onHide();
					}}
				/>
			)}
		</>
	);
}

GenerateContractModal.propTypes = {
	show: PropTypes.bool.isRequired,
	onHide: PropTypes.func.isRequired,
	entityType: PropTypes.oneOf(['contract', 'assetSale', 'asset']).isRequired,
	entityId: PropTypes.string.isRequired,
};
